import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, DropdownMenu, TableItem } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible, useHasPermission } from 'hooks';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTranslation } from 'react-i18next';
import { PiSelectionPlus } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useGetContractExtendedTemplates } from 'services/templates/contract-template';
import { IContractExtendedTemlatesSearch } from 'services/templates/contract-template/types';
import { TEMPLATE_TYPE } from 'services/templates/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { useSearchById, useSetSearchById } from 'store/state';

const ContractExtendedTemplates = () => {
  usePermissionGuard('ContractTemplatePermissionsLIST**');
  const hasPermission = useHasPermission();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);
  const filtersVisible = useComponentVisible<HTMLDivElement>(false);

  const i18nextPrefix = 'contract-template.list';

  const searchById = useSearchById();
  const setSearchById = useSetSearchById();
  const filters = searchById['ContractExtendedTemplates'] || { title: '', type: undefined };

  const templates = useGetContractExtendedTemplates(filters);

  const tableProps = useInfiniteTable(templates, {
    customPath: id => `contract/${id}/form`,
    selectable: true,
  });

  const { getSelectOptions } = useEnumToSelectOptions();

  const goToContractTemplate = (id = 'create') =>
    navigate(`/templates/contract/${id}/form`);

  const goToAgreementTemplate = (id = 'create') =>
    navigate(`agreement/${id}/form`);

  const goToCustomSectionTemplate = (id = 'create') =>
    navigate(`section/${id}/form`);

  const goToDigitalDistributionSectionTemplate = (id = 'create') =>
    navigate(`digital-distribution/${id}/form`);
  const goToPhysicalDistributionSectionTemplate = (id = 'create') =>
    navigate(`physical-distribution/${id}/form`);
  const goToMerchDistributionSectionTemplate = (id = 'create') =>
    navigate(`merch-distribution/${id}/form`);
  const goToChannelDistributionSectionTemplate = (id = 'create') =>
    navigate(`channel-distribution/${id}/form`);

  const goToCreateTemplate = (type: TEMPLATE_TYPE) => {
    type === TEMPLATE_TYPE.agreement && goToAgreementTemplate();

    type === TEMPLATE_TYPE.section && goToCustomSectionTemplate();

    type === TEMPLATE_TYPE.digitalDistribution &&
      goToDigitalDistributionSectionTemplate();

    type === TEMPLATE_TYPE.physicalDistribution &&
      goToPhysicalDistributionSectionTemplate();

    type === TEMPLATE_TYPE.merchDistribution &&
      goToMerchDistributionSectionTemplate();

    type === TEMPLATE_TYPE.channelDistribution &&
      goToChannelDistributionSectionTemplate();

    type === TEMPLATE_TYPE.contract && goToContractTemplate();
  };

  const goToTemplate = (type: TEMPLATE_TYPE, id: string) => {
    type === TEMPLATE_TYPE.agreement && goToAgreementTemplate(id);

    type === TEMPLATE_TYPE.section && goToCustomSectionTemplate(id);

    type === TEMPLATE_TYPE.digitalDistribution &&
      goToDigitalDistributionSectionTemplate(id);

    type === TEMPLATE_TYPE.physicalDistribution &&
      goToPhysicalDistributionSectionTemplate(id);

    type === TEMPLATE_TYPE.merchDistribution &&
      goToMerchDistributionSectionTemplate(id);

    type === TEMPLATE_TYPE.channelDistribution &&
      goToChannelDistributionSectionTemplate(id);

    type === TEMPLATE_TYPE.contract && goToContractTemplate(id);
  };

  const onSelectChange = (action: Action, state: State) => {
    const type = tableProps.data.nodes.find(e =>
      state.id ? e.id === state.id : e.id === state.ids[state.ids.length - 1]
    )?.type.id;
    type && goToTemplate(type, state.id);
  };

  const select = useRowSelect(tableProps.data, {
    onChange: onSelectChange,
  });

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t('contract-template.list-extended')}
          path=''
          hideBackButton
          topbarContent={
            <>
              <QueryFilter.ButtonOpener
                i18nextPrefix={''}
                onClick={filtersVisible.handleComponentVisible}
                variant={BUTTON_VARIANT.default}
                isClear
              />
              <Button
                variant={BUTTON_VARIANT.submit}
                onClick={handleComponentVisible}
                outsideChildren={
                  <DropdownMenu
                    ref={ref}
                    visible={isComponentVisible}
                    style={{ padding: 0 }}
                    justifyPosition={'end'}>
                    <div className='flex flex-col w-full'>
                      {getSelectOptions(TEMPLATE_TYPE)
                        .filter(t => t.value !== TEMPLATE_TYPE.contract)
                        .map(template => (
                          <Button
                            isClear
                            onClick={() => goToCreateTemplate(template.value)}
                            buttonStyle={{ justifyContent: 'start' }}
                            fullWidth>
                            <Button.Text
                              text={t(
                                `enum.TEMPLATE_TYPE.${
                                  TEMPLATE_TYPE[template.value]
                                }`
                              )}
                            />
                          </Button>
                        ))}
                    </div>
                  </DropdownMenu>
                }>
                <Button.Text text={t(`${i18nextPrefix}.addTemplate`)} />
                <Button.Icon icon={<PiSelectionPlus />} />
              </Button>
            </>
          }>
          <QueryFilter<IContractExtendedTemlatesSearch>
            i18nextPrefix={''}
            initialValues={filters}>
            <>
              <QueryFilter.Form<IContractExtendedTemlatesSearch>
                ref={filtersVisible.ref}
                isOpen={filtersVisible.isComponentVisible}
                onDebouncedFormChange={values => setSearchById('ContractExtendedTemplates', values)}>
                <QueryFilter.InputsGrid>
                  <FormInput.Text name='title' label={t('input.label.title')} />
                  <FormInput.Select
                    name='type'
                    label={t('input.label.type')}
                    options={getSelectOptions(
                      TEMPLATE_TYPE,
                      'enum.TEMPLATE_TYPE'
                    ).filter(o => o.value !== TEMPLATE_TYPE.contract)}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={filtersVisible.isComponentVisible}
                customLabel={data =>
                  data.key === 'type'
                    ? t(
                        `enum.TEMPLATE_TYPE.${
                          TEMPLATE_TYPE[data.value as TEMPLATE_TYPE]
                        }`
                      )
                    : `${data.value}`
                }
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table
        id={'ContractExtendedTemplates'}
        {...tableProps}
        select={select}
        displayMode
        columns={[
          {
            label: t(`input.label.title`),
            renderCell: ({ title }) => (
              <TableItem>
                <TableItem.Text text={title} />
              </TableItem>
            ),
          },
          {
            label: t(`input.label.type`),
            renderCell: ({ type }) => (
              <TableItem>
                {t(`enum.TEMPLATE_TYPE.${TEMPLATE_TYPE[type.id]}`)}
              </TableItem>
            ),
          },
        ]}
      />
    </TopbarPage>
  );
};

export default ContractExtendedTemplates;
